/**
 * Global Application Configuration
 * 
 * This file contains global settings and feature flags for the application.
 * Edit these values to control application behavior across all components.
 * 
 * @todo FUTURE ENHANCEMENT: Move runtime-configurable parameters to Redis
 *       - Create admin panel to manage config without code changes
 *       - Store in Redis hash: `app:config`
 *       - Example fields: WHALE_TAPE_MIN_ROWS, thresholds, display settings
 *       - Fallback to these defaults if Redis unavailable
 *       - Live reload config changes without rebuild
 */

export const APP_VERSION = '3.2.0';
export const SIGNAL_ENGINE_VERSION = '5.4.2';

/**
 * Development & Debug Settings
 */
export const DEV_CONFIG = {
    /**
     * Bypass Market Closed State
     *
     * When enabled, the application will continue to function even when
     * the market is closed, allowing for development and testing.
     *
     * Static NODE_ENV-based fallback only — used when the Redis-backed
     * check (isMarketBypassed() in OperationalGate.tsx, mirrors the
     * app:config:{env} BYPASS_MARKET_CLOSE key signal-engine/page.tsx
     * already reads) can't be reached. Gate consumers should prefer that
     * async check so the Redis flag is the actual, live control — this
     * constant is not meant to be edited for testing.
     */
    BYPASS_MARKET_CLOSE: process.env.NODE_ENV !== 'production',

    /**
     * Enable Debug Logging
     * 
     * When enabled, additional console logs will be output for debugging.
     * 
     * @default false
     */
    ENABLE_DEBUG_LOGS: false,

    /**
     * Show Brenda K. Brown BK Levels
     * Controlled dynamically via Redis hash app:config:{env} key SHOW_BK_LEVELS.
     * Defaults to true in development, false in production.
     */
    SHOW_BK_LEVELS: process.env.NODE_ENV !== 'production' || process.env.NEXT_PUBLIC_ENVIRONMENT === 'development',
};

/**
 * Market Hours Configuration
 * 
 * These times define when the dashboard should be active and processing data.
 * Market status (open/closed) is determined by the backend LiveData service,
 * but these times control when the dashboard allows access and data updates.
 */
export const MARKET_CONFIG = {
    /**
     * Pre-Market Start Time (EST)
     * Dashboard becomes active at 7:00 AM when OI (Open Interest) is updated
     * Format: HH:MM in 24-hour format
     */
    PRE_MARKET_START: '07:00',

    /**
     * Market Open Time (EST)
     * Regular trading session begins
     * Format: HH:MM in 24-hour format
     */
    MARKET_OPEN: '09:30',

    /**
     * Market Close Time (EST)
     * Regular trading session ends
     * Format: HH:MM in 24-hour format
     */
    MARKET_CLOSE: '16:00',

    /**
     * Post-Market End Time (EST)
     * Dashboard remains active until 5:00 PM when everything settles
     * Data is still moving between 4:00 PM (market close) and 5:00 PM
     * Format: HH:MM in 24-hour format
     */
    POST_MARKET_END: '17:00',

    /**
     * Maintenance Mode
     * When true, displays maintenance screen to all users
     * Set to true for testing/development, false for production
     */
    MAINTENANCE_MODE: false,
};

/**
 * Data Refresh Configuration
 */
export const DATA_CONFIG = {
    /**
     * Default polling interval for real-time data (milliseconds)
     */
    DEFAULT_POLL_INTERVAL: 30000, // 30 seconds

    /**
     * Fast polling interval for critical data (milliseconds)
     */
    FAST_POLL_INTERVAL: 5000, // 5 seconds

    /**
     * Slow polling interval for non-critical data (milliseconds)
     */
    SLOW_POLL_INTERVAL: 60000, // 60 seconds

    /**
     * Maximum age for cached data before refresh (milliseconds)
     */
    MAX_CACHE_AGE: 300000, // 5 minutes
};

/**
 * Feature Flags
 */
export const FEATURES = {
    /**
     * Enable 3D Visualizations
     */
    ENABLE_3D_VISUALIZER: true,

    /**
     * Enable AI Analysis
     */
    ENABLE_AI_ANALYSIS: true,

    /**
     * Enable Whale Feed
     */
    ENABLE_WHALE_FEED: true,

    /**
     * Enable Chart Annotations
     */
    ENABLE_CHART_ANNOTATIONS: true,


    /**
     * Enable Live Trading
     */
    ENABLE_LIVE_TRADING_PRODUCTION: false,

    /**
     * Enable Live Trading
     */
    ENABLE_LIVE_TRADING_DEVELOPMENT: true,

    /**
     * Signal Engine V4 chart — HIST (level-history) button.
     * Not ready for prod yet; visible in dev only until validated.
     */
    V4_HIST_SHOW: process.env.NODE_ENV === 'development',

    /**
     * Signal Engine V4 chart — OHLC candle backfill toggle (dev01's raw
     * candlestick feed, past 1-30 days). Client-side gate only; the server
     * route /api/chart/candle-history also checks CANDLE_HISTORY_ENABLED and
     * 404s if unset, so this stays a no-op unless both are on.
     *
     * Validated and enabled in production 2026-08-28 — was dev-only since
     * 2026-08-10 pending validation.
     */
    V4_CANDLE_HISTORY_SHOW: true,


    /**
     * Whale Tape - Minimum visible rows
     * Controls the fixed height of the whale tape display
     * Change this value to adjust without rebuilding
     * 
     * @default 10
     * @todo Move to Redis-based admin config for runtime editing
     */
    WHALE_TAPE_MIN_ROWS: 10,

    /**
     * Mini Graphs - Initial View State
     * Controls whether each graph starts collapsed or expanded (mini)
     * 
     * Options: 'collapsed' | 'mini'
     * - 'collapsed': Single line with key metrics only
     * - 'mini': Small bar chart visible
     * 
     * @default collapsed for all
     * @todo Move to Redis-based admin config for runtime editing
     */
    MINIGRAPH_VOLUME_INITIAL_STATE: 'collapsed' as 'collapsed' | 'mini',
    MINIGRAPH_OI_INITIAL_STATE: 'collapsed' as 'collapsed' | 'mini',
    MINIGRAPH_GEX_INITIAL_STATE: 'mini' as 'collapsed' | 'mini',
    MINIGRAPH_DEX_INITIAL_STATE: 'mini' as 'collapsed' | 'mini',
    MINIGRAPH_CEX_INITIAL_STATE: 'mini' as 'collapsed' | 'mini',
};

/**
 * API Configuration
 */
export const API_CONFIG = {
    /**
     * Request timeout (milliseconds)
     */
    REQUEST_TIMEOUT: 30000, // 30 seconds

    /**
     * Maximum retry attempts for failed requests
     */
    MAX_RETRIES: 3,

    /**
     * Retry delay (milliseconds)
     */
    RETRY_DELAY: 1000, // 1 second
};

/**
 * UI Configuration
 */
export const UI_CONFIG = {
    /**
     * Default theme
     */
    DEFAULT_THEME: 'dark' as const,

    /**
     * Enable animations
     */
    ENABLE_ANIMATIONS: true,

    /**
     * Toast notification duration (milliseconds)
     */
    TOAST_DURATION: 5000, // 5 seconds

    /**
     * Maximum number of items to display in lists before pagination
     */
    MAX_LIST_ITEMS: 50,
};

/**
 * Ticker-to-Symbol Mapping for External Charts (TradingView)
 * Maps internal dashboard tickers to their corresponding chart symbols.
 */
export const TICKER_SYMBOL_MAP: Record<string, string> = {
    'SPX': 'OANDA:SPX500USD',
    'SPY': 'SPY',
    'QQQ': 'QQQ',
    'ES': '',
    'NQ': '',
    'IWM': 'IWM',
    "TSLA": "TSLA"
};

/**
 * Helper function to check if market bypass is enabled
 */
export const isMarketBypassEnabled = () => DEV_CONFIG.BYPASS_MARKET_CLOSE;

/**
 * Helper function to check if debug logging is enabled
 */
export const isDebugEnabled = () => DEV_CONFIG.ENABLE_DEBUG_LOGS;

/**
 * Helper function to log debug messages
 */
export const debugLog = (...args: any[]) => {
    if (isDebugEnabled()) {
        console.log('[DEBUG]', ...args);
    }
};

/**
 * Helper function to detect if running in development mode
 * @returns true if in development, false if in production
 */
export const isDevelopmentMode = (): boolean => {
    return process.env.NODE_ENV === 'development';
};


/**
 * Helper function to check if Live Trading features should be enabled
 * Based on environment and feature flags
 */
export const isLiveTradingEnabled = (): boolean => {
    if (isDevelopmentMode()) {
        return FEATURES.ENABLE_LIVE_TRADING_DEVELOPMENT;
    } else {
        return FEATURES.ENABLE_LIVE_TRADING_PRODUCTION;
    }
};

// Parses a Date into its ET wall-clock components. Do not re-wrap the result
// in `new Date()` — that reinterprets the string in the caller's local zone.
const getETParts = (date: Date) => {
    const etStr = date.toLocaleString('sv', { timeZone: 'America/New_York' });
    const [datePart, timePart] = etStr.split(' ');
    const [h, m] = timePart.split(':').map(Number);
    // getUTCDay(), not getDay() — .getDay() reads the caller's local
    // timezone and would flip the weekday for anyone far from ET.
    const day = new Date(`${datePart}T00:00:00Z`).getUTCDay();
    return { day, hours: h, minutes: m };
};

/**
 * Helper function to check if current time is within dashboard active hours
 * Dashboard is active from PRE_MARKET_START (7:00 AM) to POST_MARKET_END (5:00 PM) EST
 *
 * @param currentTime - Optional Date object (defaults to now)
 * @returns true if within active hours, false otherwise
 */
export const isDashboardActiveHours = (currentTime: Date = new Date()): boolean => {
    // If bypass is enabled, always return true

    console.log('isMarketBypassEnabled() :', isMarketBypassEnabled());
    if (isMarketBypassEnabled()) {
        return true;
    }

    const { day, hours, minutes } = getETParts(currentTime);

    // Check for weekends (0 = Sunday, 6 = Saturday)
    if (day === 0 || day === 6) {
        return false;
    }

    const currentMinutes = hours * 60 + minutes;

    // Parse config times (format: "HH:MM")
    const parseTime = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const preMarketStart = parseTime(MARKET_CONFIG.PRE_MARKET_START);
    const postMarketEnd = parseTime(MARKET_CONFIG.POST_MARKET_END);

    return currentMinutes >= preMarketStart && currentMinutes <= postMarketEnd;
};

/**
 * Helper function to check if current time is during regular market hours
 * Regular market hours: MARKET_OPEN (9:30 AM) to MARKET_CLOSE (4:00 PM) EST
 * 
 * @param currentTime - Optional Date object (defaults to now)
 * @returns true if during regular market hours, false otherwise
 */
export const isRegularMarketHours = (currentTime: Date = new Date()): boolean => {
    // If bypass is enabled, always return true
    if (isMarketBypassEnabled()) {
        return true;
    }

    const { day, hours, minutes } = getETParts(currentTime);

    // Check for weekends
    if (day === 0 || day === 6) {
        return false;
    }

    const currentMinutes = hours * 60 + minutes;

    const parseTime = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const marketOpen = parseTime(MARKET_CONFIG.MARKET_OPEN);
    const marketClose = parseTime(MARKET_CONFIG.MARKET_CLOSE);

    return currentMinutes >= marketOpen && currentMinutes <= marketClose;
};

/**
 * Helper function to check if current time is during pre-market hours
 * Pre-market: PRE_MARKET_START (7:00 AM) to MARKET_OPEN (9:30 AM) EST
 * 
 * @param currentTime - Optional Date object (defaults to now)
 * @returns true if during pre-market hours, false otherwise
 */
export const isPreMarketHours = (currentTime: Date = new Date()): boolean => {
    const { day, hours, minutes } = getETParts(currentTime);
    if (day === 0 || day === 6) {
        return false;
    }

    const currentMinutes = hours * 60 + minutes;

    const parseTime = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const preMarketStart = parseTime(MARKET_CONFIG.PRE_MARKET_START);
    const marketOpen = parseTime(MARKET_CONFIG.MARKET_OPEN);

    return currentMinutes >= preMarketStart && currentMinutes < marketOpen;
};

/**
 * Helper function to check if current time is during post-market hours
 * Post-market: MARKET_CLOSE (4:00 PM) to POST_MARKET_END (5:00 PM) EST
 * 
 * @param currentTime - Optional Date object (defaults to now)
 * @returns true if during post-market hours, false otherwise
 */
export const isPostMarketHours = (currentTime: Date = new Date()): boolean => {
    const { day, hours, minutes } = getETParts(currentTime);
    if (day === 0 || day === 6) {
        return false;
    }

    const currentMinutes = hours * 60 + minutes;

    const parseTime = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const marketClose = parseTime(MARKET_CONFIG.MARKET_CLOSE);
    const postMarketEnd = parseTime(MARKET_CONFIG.POST_MARKET_END);

    return currentMinutes >= marketClose && currentMinutes <= postMarketEnd;
};

